import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { getTenantToday } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  RefreshCcw,
  Activity,
  ChevronRight,
  Zap,
  TrendingUp,
  TrendingDown,
  BarChart3,
  History,
  Settings,
  MoreVertical,
  AlertCircle,
  Clock,
  Layers,
  Cpu,
  Bot,
  Sparkles,
  Search,
  AlertTriangle,
  DollarSign
} from 'lucide-react';
import { cn } from '../lib/utils';

import { DateField } from './shared/DateField';

// ============== Types ==============
type HedgeCategoryKey = 'rate' | 'credit' | 'liquidity';

interface HedgeCategoryDef {
  key: HedgeCategoryKey;
  label: string;
  icon: string;
  description: string;
}

interface CreditHedge {
  referenceEntity: string;
  notional: number;
  hedgeRatio: number;
  rationale: string;
  type?: string;
}

interface LiquidityAssessment {
  availableCash: number;
  projectedCapitalCalls6Mo: number;
  recommendedCashReserve: number;
  liquidityWarning: boolean;
  warningMessage?: string;
  totalUnfunded?: number;
  projectionBasis?: string;
}

// ============== Constants ==============
const HEDGE_CATEGORIES: HedgeCategoryDef[] = [
  { key: 'rate', label: 'Rate', icon: '📈', description: 'Interest rate swaps and duration hedges' },
  { key: 'credit', label: 'Credit', icon: '🛡️', description: 'CDS, CDX index overlays' },
  { key: 'liquidity', label: 'Liquidity', icon: '💰', description: 'Cash reserve and financing recommendations' },
];

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Styled Components ==============
const Container = styled.div`
  display: flex;
  height: calc(100vh - 64px);
  background: transparent;
`;

const Sidebar = styled.div`
  width: 360px;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2.5rem;
  background: transparent;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const RiskSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin: 20px 0;
`;

const RiskCard = styled.div`
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 16px;
`;

const PortfolioHedgingScreen: React.FC = () => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState<any[]>([]);

  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(getTenantToday());
  const [hedgeAnalysis, setHedgeAnalysis] = useState<any>(null);
  const [selectedHedge, setSelectedHedge] = useState<any>(null);
  const [hedgeImpact, setHedgeImpact] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hedgeCategory, setHedgeCategory] = useState<HedgeCategoryKey>('rate');

  useEffect(() => {
    fetchPortfolios();
  }, []);

  useEffect(() => {
    if (selectedPortfolioId) {
      analyzeHedgeRequirements();
    }
  }, [selectedPortfolioId, selectedDate]);

  const fetchPortfolios = async () => {
    try {
      const response = await axios.get('/api/dimensions/portfolios', {
        params: { tradingEnabled: true },
      });
      const data = response.data?.content || response.data || [];
      setPortfolios(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError('Failed to load portfolios: ' + (err.response?.data?.error || err.message));
    }
  };

  const analyzeHedgeRequirements = async () => {
    if (!selectedPortfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const url = `/api/portfolio-hedging/${selectedPortfolioId}/analyze${selectedDate ? `?asOfDate=${selectedDate}` : ''}`;
      const response = await axios.get(url);
      setHedgeAnalysis(response.data);
      setSelectedHedge(null);
      setHedgeImpact(null);
    } catch (err: any) {
      setError('Failed to analyze hedge requirements: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const calculateHedgeImpact = async (hedge: any) => {
    if (!selectedPortfolioId || !hedge) return;
    setLoading(true);
    setError(null);
    try {
      const url = `/api/portfolio-hedging/${selectedPortfolioId}/calculate-impact${selectedDate ? `?asOfDate=${selectedDate}` : ''}`;
      const response = await axios.post(url, {
        hedgeType: hedge.hedgeType,
        hedgeSecurityIdentifier: hedge.identifier,
        hedgeNotional: hedge.notionalAmount
      });
      setHedgeImpact(response.data);
      setSelectedHedge(hedge);
    } catch (err: any) {
      setError('Failed to calculate hedge impact: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleProposeHedge = (hedge: any) => {
    const portfolio = portfolios.find((p: any) => p.id === selectedPortfolioId || String(p.id) === String(selectedPortfolioId));
    const portfolioName = portfolio?.portfolioName || '';

    const routeMap: Record<string, string> = {
      'Interest Rate Swap': '/trade-entry/derivatives/ir-swap',
      'IRS': '/trade-entry/derivatives/ir-swap',
      'Put Option': '/trade-entry/derivatives/options',
      'Option': '/trade-entry/derivatives/options'
    };

    const route = routeMap[hedge.hedgeType] || '/trade-entry/derivatives/options';
    navigate(route, {
      state: {
        hedgeData: {
          ...hedge,
          securityName: hedge.securityName || hedge.identifier,
          identifier: hedge.identifier
        },
        portfolioId: selectedPortfolioId,
        portfolioName: portfolioName
      }
    });
  };

  // ============== Derived data (all from the backend response — F-HG-010:
  // no client-side fabrications) ==============
  const totalMarketValue = parseFloat(hedgeAnalysis?.totalMarketValue || 0);
  const creditHedges: CreditHedge[] = hedgeAnalysis?.creditHedges || [];
  const liquidityAssessment: LiquidityAssessment | null = hedgeAnalysis?.liquidityAssessment || null;

  const portfolioDuration = hedgeAnalysis?.portfolioDuration != null ? Number(hedgeAnalysis.portfolioDuration) : null;
  const targetDuration = hedgeAnalysis?.targetDuration != null ? Number(hedgeAnalysis.targetDuration) : null;
  const durationGap = portfolioDuration != null && targetDuration != null ? portfolioDuration - targetDuration : null;
  // Real underlying loan/CLO exposure from the engine — the old derivation
  // summed suggested HEDGE notionals under a "par value" caption.
  const creditExposurePar = hedgeAnalysis?.creditExposurePar != null
    ? Number(hedgeAnalysis.creditExposurePar)
    : creditHedges.reduce((sum, h) => sum + (h.notional || 0), 0);
  const cdsCount = creditHedges.filter(h => h.type !== 'cdx_index').length;

  const availableCash = liquidityAssessment?.availableCash != null ? Number(liquidityAssessment.availableCash) : null;
  const totalUnfunded = liquidityAssessment?.totalUnfunded || 0;

  // ============== Render helpers ==============
  const renderRiskSummaryCards = () => (
    <RiskSummaryGrid>
      <RiskCard>
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Duration vs Target</div>
        <div className="text-xl font-bold text-white">
          {portfolioDuration != null ? `${portfolioDuration.toFixed(4)}y` : '—'}
          {targetDuration != null && targetDuration > 0 && (
            <span className="text-sm font-semibold text-slate-400"> / {targetDuration.toFixed(2)}y</span>
          )}
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          {durationGap == null || targetDuration === 0
            ? 'No duration target configured'
            : durationGap > 0.005
              ? `${durationGap.toFixed(2)}y OVER target — shed duration`
              : durationGap < -0.005
                ? `${Math.abs(durationGap).toFixed(2)}y UNDER target — rate hedges would widen the gap`
                : 'At target'}
        </div>
      </RiskCard>
      <RiskCard>
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Credit Exposure</div>
        <div className="text-xl font-bold text-white">
          ${creditExposurePar.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          Loan/CLO market value{creditHedges.length > 0 ? ` · ${cdsCount} CDS suggestion${cdsCount !== 1 ? 's' : ''}` : ''}
        </div>
      </RiskCard>
      <RiskCard>
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Available Cash</div>
        <div className="text-xl font-bold text-white">
          {availableCash != null
            ? `$${availableCash.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
            : '—'}
        </div>
        <div className="text-[10px] text-slate-500 mt-1">Cash sleeve at market value</div>
      </RiskCard>
      <RiskCard>
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Unfunded Commitment Risk</div>
        <div className="text-xl font-bold text-white">
          ${totalUnfunded.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          {liquidityAssessment?.projectionBasis === 'COMMITMENT_ENGINE'
            ? 'Commitment engine' : 'Projection unavailable'}
        </div>
      </RiskCard>
    </RiskSummaryGrid>
  );

  const renderCategoryTabs = () => (
    <div className="flex items-center gap-2 flex-wrap">
      {HEDGE_CATEGORIES.map((cat) => (
        <button
          key={cat.key}
          onClick={() => setHedgeCategory(cat.key)}
          title={cat.description}
          className={cn(
            "px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2",
            hedgeCategory === cat.key
              ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
              : "text-slate-500 hover:text-slate-300"
          )}
        >
          <span>{cat.icon}</span>
          {cat.label}
        </button>
      ))}
    </div>
  );

  const renderCreditTab = () => {
    if (!creditHedges.length) {
      return (
        <GlassCard className="p-6">
          <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
            No credit hedge suggestions available. The portfolio may not have significant credit exposure.
          </div>
        </GlassCard>
      );
    }

    const cdxOverlay = creditHedges.find(h => h.type === 'cdx_index');
    const cdsSuggestions = creditHedges.filter(h => h.type !== 'cdx_index');

    return (
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-5 h-5 text-indigo-400" />
          <h3 className="text-lg font-bold text-white">Credit Hedge Suggestions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest py-3 px-4">Reference Entity</th>
                <th className="text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest py-3 px-4">Notional</th>
                <th className="text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest py-3 px-4">Hedge Ratio</th>
                <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest py-3 px-4">Rationale</th>
              </tr>
            </thead>
            <tbody>
              {cdsSuggestions.map((hedge, idx) => (
                <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 font-medium text-white">{hedge.referenceEntity}</td>
                  <td className="py-3 px-4 text-right font-mono text-blue-400">
                    ${(hedge.notional || 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-emerald-400">
                    {((hedge.hedgeRatio || 0) * 100).toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-xs">{hedge.rationale}</td>
                </tr>
              ))}
              {cdxOverlay && (
                <tr className="bg-indigo-500/5 border-b border-indigo-500/20">
                  <td className="py-3 px-4 font-medium text-white">
                    <div className="flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-400 text-[10px] font-bold uppercase tracking-wider">CDX Index</span>
                      {cdxOverlay.referenceEntity}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-blue-400">
                    ${(cdxOverlay.notional || 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-emerald-400">
                    {((cdxOverlay.hedgeRatio || 0) * 100).toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-xs">{cdxOverlay.rationale}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>
    );
  };

  const renderLiquidityTab = () => {
    if (!liquidityAssessment) {
      return (
        <GlassCard className="p-6">
          <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
            No liquidity assessment available for this portfolio.
          </div>
        </GlassCard>
      );
    }

    const { availableCash, projectedCapitalCalls6Mo, recommendedCashReserve, liquidityWarning, warningMessage } = liquidityAssessment;

    return (
      <div className="space-y-6">
        {liquidityWarning && (
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-amber-300">Liquidity Warning</div>
              <div className="text-xs text-amber-400/80 mt-1">
                {warningMessage || 'Cash reserves may be insufficient to meet projected commitments.'}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <GlassCard className="p-6 border-l-4" style={{ borderLeftColor: '#10b981' }}>
            <div className="flex justify-between items-start">
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Available Cash</div>
                <div className="text-2xl font-bold text-white">
                  ${(availableCash || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-emerald-400 border border-white/5">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4" style={{ borderLeftColor: '#f59e0b' }}>
            <div className="flex justify-between items-start">
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Projected Capital Calls (6M)</div>
                <div className={cn("text-2xl font-bold", projectedCapitalCalls6Mo > availableCash ? "text-red-400" : "text-white")}>
                  ${(projectedCapitalCalls6Mo || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-amber-400 border border-white/5">
                <Clock className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4" style={{ borderLeftColor: liquidityWarning ? '#ef4444' : '#3b82f6' }}>
            <div className="flex justify-between items-start">
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Recommended Cash Reserve</div>
                <div className={cn("text-2xl font-bold", liquidityWarning ? "text-red-400" : "text-white")}>
                  ${(recommendedCashReserve || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className={cn(
                "w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center border border-white/5",
                liquidityWarning ? "text-red-400" : "text-blue-400"
              )}>
                <AlertCircle className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    );
  };

  const renderRateTab = () => (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Activity className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-bold text-white">Risk Requirements</h3>
        </div>
        <div className="space-y-4">
          {hedgeAnalysis.hedgeRequirements?.map((req: any, idx: number) => (
            <div key={idx} className="flex items-center justify-between p-4 bg-slate-950/30 border border-white/5 rounded-2xl">
              <div>
                <div className="text-sm font-bold text-white">{req.riskType}</div>
                <div className="text-xs text-slate-500 mt-0.5">{req.description}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-mono font-bold text-blue-400">{req.currentValue}</div>
              </div>
            </div>
          )) || (
            <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
              No specific hedge requirements identified
            </div>
          )}
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Zap className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-bold text-white">Suggested Hedges</h3>
        </div>
        <div className="space-y-3">
          {hedgeAnalysis.hedgeSuggestions?.map((suggestion: any, idx: number) => (
            <div
              key={idx}
              onClick={() => calculateHedgeImpact(suggestion)}
              className={cn(
                "flex items-center justify-between p-4 border rounded-2xl group transition-all cursor-pointer",
                selectedHedge?.identifier === suggestion.identifier
                  ? "bg-blue-600/10 border-blue-500/50 shadow-inner shadow-blue-500/10"
                  : "bg-slate-950/30 border-white/5 hover:bg-white/5 hover:border-white/10"
              )}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center shadow-lg">
                  <Cpu className={cn("w-5 h-5", selectedHedge?.identifier === suggestion.identifier ? "text-blue-400" : "text-slate-500")} />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{suggestion.securityName}</div>
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-400 text-[10px] font-bold uppercase tracking-wider">{suggestion.hedgeType}</span>
                    <span className="text-[10px] text-slate-500 font-medium">${parseFloat(suggestion.notionalAmount || 0).toLocaleString()} Notional</span>
                  </div>
                </div>
              </div>
              <ChevronRight className={cn(
                "w-4 h-4 transition-all",
                selectedHedge?.identifier === suggestion.identifier ? "text-blue-400 translate-x-1" : "text-slate-600 group-hover:text-slate-400"
              )} />
            </div>
          )) || (
            <div className="py-12 text-center text-slate-500 italic text-sm">No hedge suggestions available</div>
          )}
        </div>
      </GlassCard>
    </div>
  );

  const renderEmptyCategory = (category: HedgeCategoryDef) => (
    <GlassCard className="p-6">
      <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
        No {category.label.toLowerCase()} hedge data available. {category.description}
      </div>
    </GlassCard>
  );

  const renderCategoryContent = () => {
    const category = HEDGE_CATEGORIES.find(c => c.key === hedgeCategory);
    if (!category) return null;

    switch (hedgeCategory) {
      case 'rate':
        return renderRateTab();
      case 'credit':
        return renderCreditTab();
      case 'liquidity':
        return renderLiquidityTab();
      default:
        return null;
    }
  };

  return (
    <Container>
      <Sidebar>
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Hedging</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Risk Management</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Step 1: Select Portfolio */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <StepNumber>1</StepNumber>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Select Portfolio</h3>
            </div>
            <select
              value={selectedPortfolioId}
              onChange={(e) => setSelectedPortfolioId(e.target.value)}
              className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none cursor-pointer"
            >
              <option value="">Choose a portfolio...</option>
              {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
            </select>
          </div>

          {/* Step 2: Analysis Date */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <StepNumber>2</StepNumber>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Analysis Date</h3>
            </div>
            <DateField
              value={selectedDate ?? ''}
              className="w-full"
              onChange={iso => setSelectedDate(iso)}
            />
          </div>

          {/* Step 3: Actions */}
          <div className="space-y-4 pt-4 border-t border-white/5">
            <div className="flex items-center gap-3">
              <StepNumber>3</StepNumber>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Actions</h3>
            </div>
            <button
              onClick={analyzeHedgeRequirements}
              disabled={loading || !selectedPortfolioId}
              className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
            >
              {loading ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
              Analyze Requirements
            </button>
          </div>
        </div>

        <div className="p-4 border-t border-white/5 bg-slate-950/20">
          <button className="w-full py-3 px-4 bg-slate-900 hover:bg-slate-800 border border-white/5 rounded-xl text-xs font-bold text-slate-400 hover:text-white transition-all flex items-center justify-center gap-2">
            <History className="w-4 h-4" />
            Hedge History
          </button>
        </div>
      </Sidebar>

      <MainContent>
        <AnimatePresence mode="wait">
          {!hedgeAnalysis ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full flex flex-col items-center justify-center text-center space-y-6"
            >
              <div className="w-20 h-20 rounded-3xl bg-blue-500/5 border border-blue-500/10 flex items-center justify-center text-blue-500/20">
                <Shield className="w-10 h-10" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Hedge Surveillance</h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
                  Select a portfolio to analyze duration exposure and generate derivative hedging recommendations.
                </p>
              </div>
              {/* F-HG-002/U18: a failed FIRST analysis used to leave this idle
                  empty state with no error anywhere on the main pane. */}
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm flex items-center gap-3 max-w-lg">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="results"
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-8"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Activity className="w-4 h-4 text-blue-400" />
                    </div>
                    Hedge Analysis Core
                  </h2>
                  <p className="text-slate-500 mt-1">
                    Risk metrics for {portfolios.find(p => p.id.toString() === selectedPortfolioId)?.portfolioName}
                  </p>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { label: 'Total Market Value', value: `$${parseFloat(hedgeAnalysis.totalMarketValue || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, icon: <TrendingUp className="w-5 h-5" />, color: '#3b82f6' },
                  { label: 'Portfolio Duration', value: `${parseFloat(hedgeAnalysis.portfolioDuration || 0).toFixed(4)}y`, icon: <Clock className="w-5 h-5" />, color: '#8b5cf6' },
                  { label: 'Active Positions', value: String(hedgeAnalysis.numberOfPositions || 0), icon: <Layers className="w-5 h-5" />, color: '#10b981' }
                ].map((stat, i) => (
                  <GlassCard key={i} className="p-6 border-l-4" style={{ borderLeftColor: stat.color }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">{stat.label}</div>
                        <div className="text-2xl font-bold text-white">{stat.value}</div>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 border border-white/5">
                        {stat.icon}
                      </div>
                    </div>
                  </GlassCard>
                ))}
              </div>

              {/* Private Asset Risk Summary */}
              {renderRiskSummaryCards()}

              {/* Hedge Category Tabs */}
              <div className="flex items-center justify-between">
                {renderCategoryTabs()}
              </div>

              {/* Category Content */}
              {renderCategoryContent()}

              <AnimatePresence>
                {hedgeImpact && selectedHedge && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <div className="h-px bg-white/5" />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="w-6 h-6 text-emerald-400" />
                        <h3 className="text-xl font-bold text-white tracking-tight">Hedge Impact Analysis</h3>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => { setHedgeImpact(null); setSelectedHedge(null); }}
                          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl transition-all"
                        >
                          Clear Analysis
                        </button>
                        <button
                          onClick={() => handleProposeHedge(selectedHedge)}
                          className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-emerald-600/20 uppercase tracking-widest"
                        >
                          Propose Hedge Trade
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      {[
                        { label: 'Current Duration', value: `${parseFloat(hedgeImpact.currentDuration || 0).toFixed(4)}y`, sub: 'Pre-hedge' },
                        { label: 'New Duration', value: `${parseFloat(hedgeImpact.newDuration || 0).toFixed(4)}y`, sub: 'Post-hedge', highlight: true },
                        { label: 'Net Change', value: `${parseFloat(hedgeImpact.durationChange || 0).toFixed(4)}y`, sub: 'Absolute drift', color: parseFloat(hedgeImpact.durationChange || 0) < 0 ? 'text-red-400' : 'text-emerald-400' },
                        { label: 'Reduction %', value: `${parseFloat(hedgeImpact.durationChangePercent || 0).toFixed(2)}%`, sub: 'Relative impact', color: parseFloat(hedgeImpact.durationChangePercent || 0) < 0 ? 'text-red-400' : 'text-emerald-400' }
                      ].map((metric, i) => (
                        <div key={i} className="p-5 bg-slate-950/30 border border-white/5 rounded-2xl">
                          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">{metric.label}</div>
                          <div className={cn("text-xl font-bold", metric.color || "text-white")}>{metric.value}</div>
                          <div className="text-[10px] text-slate-600 font-medium mt-1">{metric.sub}</div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </MainContent>
    </Container>
  );
};

export default PortfolioHedgingScreen;
